import { useMemo } from 'react';
import { useAppSelector } from '../../../../reduxService/hooks';
import { RootState } from '../../../../reduxService';
import { IPdfProjectInfo, hasValidProjectInfo } from '../types';

/**
 * Memoized selectors for performance optimization
 */
const selectDynamicProject = (state: RootState) => state.Dynamic.project;

const selectResultsData = (state: RootState): unknown => state.Dynamic.results.data?.outputData;

const selectAashtoResults = (state: RootState): unknown => state.Dynamic.project.data?.aashtoResults;

/**
 * ButtonPanel state interface
 */
export interface IButtonPanelState {
    projectInfo: { data: IPdfProjectInfo } | null;
    resultsData: unknown;
    aashtoResults: unknown;
    projectType: string | null;
    projectGuid: string | null;
    projectName: string | null;
    dynamicProject: ReturnType<typeof selectDynamicProject>;
    hasValidData: boolean;
}

/**
 * Custom hook for ButtonPanel state management with memoization
 * Centralizes Redux selectors and provides optimized state access
 */
export const useButtonPanelState = (): IButtonPanelState => {
    const dynamicProject = useAppSelector(selectDynamicProject);
    const resultsData = useAppSelector(selectResultsData);
    const aashtoResults = useAppSelector(selectAashtoResults);

    return useMemo(() => {
        // Validate and extract project info
        const projectInfo = dynamicProject.data?.projectInfo;
        const validProjectInfo = hasValidProjectInfo(projectInfo) ? projectInfo : null;

        // Extract key project data
        const projectType = dynamicProject.data?.type || null;
        const projectGuid = dynamicProject.data?.guid || null;
        const projectName = dynamicProject.data?.projectName || null;

        // Validate overall data state
        const hasValidData = Boolean(validProjectInfo && dynamicProject.data);

        return {
            projectInfo: validProjectInfo,
            resultsData,
            aashtoResults,
            projectType,
            projectGuid,
            projectName,
            dynamicProject,
            hasValidData,
        };
    }, [dynamicProject, resultsData, aashtoResults]);
};

/**
 * Hook for geotextile selection validation (Unpaved projects)
 * Memoized to prevent unnecessary recalculations
 */
export const useGeotextileSelection = (projectInfo: { data: IPdfProjectInfo } | null): boolean => {
    return useMemo(() => {
        if (!projectInfo?.data) return false;

        const unpavedData = projectInfo.data;

        // FLAT structure: geoTextile is directly on unpavedData
        const geoTextile = (unpavedData as any).geoTextile;

        if (!geoTextile || typeof geoTextile !== 'object') return false;

        const selectedGeotextile = (geoTextile as Record<string, unknown>).selectedGeotextile;

        // Ensure we always return a boolean, never a string or undefined
        return Boolean(selectedGeotextile && typeof selectedGeotextile === 'string' && selectedGeotextile.trim() !== '');
    }, [projectInfo]);
};
